/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Feature Activation Framework — core types.
 *
 * `Feature` is the single closed set of features the app can gate on. Every
 * entry MUST have a matching definition in `featureRegistry` (see registry.ts):
 * the discriminated `TFeatureDefinition` union is what makes an entry either
 * lifecycle-only (`CLIENT_ONLY`) or additionally service-gated
 * (`SERVICE_CONTROLLED`).
 */

/** The set of features application code can gate activation on. */
export const Feature = {
  NPVR_RECORDING: "NPVR_RECORDING"
} as const;

export type Feature = (typeof Feature)[keyof typeof Feature];

/**
 * Lifecycle stage of a feature.
 *
 * `DISABLED`/`IN_DEVELOPMENT`/`IN_TESTING` are confined to non-production
 * environments by the stage-visibility resolver — a service value must never
 * widen those lifecycle restrictions. `RELEASED` is the one production-visible
 * stage: it lets a shipped, service-controlled feature (e.g. NPVR recording)
 * have its activation driven by live service/cached state in production, while
 * the stage gate still governs everything below it.
 */
export const FeatureStage = {
  DISABLED: "DISABLED",
  IN_DEVELOPMENT: "IN_DEVELOPMENT",
  IN_TESTING: "IN_TESTING",
  RELEASED: "RELEASED"
} as const;

export type FeatureStage = (typeof FeatureStage)[keyof typeof FeatureStage];

/**
 * The stages a debug **Stage Override** may select, in the order the debug
 * screen renders and cycles them.
 *
 * `RELEASED` is deliberately absent. It is the one production-visible stage, so
 * offering it as an override would be the single way a QE/developer setting
 * could widen a lifecycle restriction — exactly the property
 * `resolveStageVisibility` exists to guarantee. Every overridable stage is
 * strictly non-production, so a stage override can only ever narrow visibility
 * or move a feature between two invisible-in-production stages.
 */
export const OVERRIDABLE_FEATURE_STAGES: ReadonlyArray<FeatureStage> = [
  FeatureStage.DISABLED,
  FeatureStage.IN_DEVELOPMENT,
  FeatureStage.IN_TESTING
];

/** True when `value` is a stage a Stage Override is allowed to select. */
export function isOverridableFeatureStage(
  value: unknown
): value is FeatureStage {
  return (
    typeof value === "string" &&
    OVERRIDABLE_FEATURE_STAGES.indexOf(value as FeatureStage) !== -1
  );
}

/** How a feature's activation is ultimately decided. */
export const FeatureType = {
  CLIENT_ONLY: "CLIENT_ONLY",
  SERVICE_CONTROLLED: "SERVICE_CONTROLLED"
} as const;

export type FeatureType = (typeof FeatureType)[keyof typeof FeatureType];

/**
 * The build environment the resolver reasons about. Independent of
 * webpack/NODE_ENV: it is derived once from the dev-tools build flag.
 */
export const BuildEnvironment = {
  DEVELOPMENT: "DEVELOPMENT",
  PRODUCTION: "PRODUCTION"
} as const;

export type BuildEnvironment =
  (typeof BuildEnvironment)[keyof typeof BuildEnvironment];

/**
 * The external source that controls a SERVICE_CONTROLLED feature. `DeviceFeatures`
 * is just one source; NPVR (and future services) are peers. `FeatureActivation`
 * never learns how any of these work — a per-source provider does.
 */
export const FeatureServiceSource = {
  DEVICE_FEATURES: "DEVICE_FEATURES",
  NPVR: "NPVR"
} as const;

export type FeatureServiceSource =
  (typeof FeatureServiceSource)[keyof typeof FeatureServiceSource];

/**
 * Declares which external source controls a SERVICE_CONTROLLED feature.
 *
 * `source`        — which provider supplies the state (DeviceFeatures, NPVR, …).
 * `featureName`   — DeviceFeatures key; omitted for sources that need no key
 *                   (e.g. NPVR, whose single response is the state).
 * `attributeName` — DeviceFeatures boolean attribute; omit for a presence check.
 * `defaultEnabled`— safe default when neither fresh nor cached state exists.
 *                   Defaults to `false` (fail closed) when omitted.
 */
export interface TServiceFeatureMapping {
  source: FeatureServiceSource;
  featureName?: string;
  attributeName?: string;
  defaultEnabled?: boolean;
}

/** A feature whose activation depends only on lifecycle stage + environment. */
export interface TClientOnlyFeatureDefinition {
  type: typeof FeatureType.CLIENT_ONLY;
  stage: FeatureStage;
  serviceFeature?: never;
}

/** A feature that additionally requires a device/service feature value. */
export interface TServiceControlledFeatureDefinition {
  type: typeof FeatureType.SERVICE_CONTROLLED;
  stage: FeatureStage;
  serviceFeature: TServiceFeatureMapping;
}

export type TFeatureDefinition =
  | TClientOnlyFeatureDefinition
  | TServiceControlledFeatureDefinition;
